import type { Prisma, PrismaClient } from '@prisma/client';
import { Router, type Request } from 'express';
import { z } from 'zod';
import { audit } from '../../core/audit.js';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp, parse } from '../../core/http.js';
import { idempotent } from '../../core/idempotency.js';
import { authenticate, permissionsGranted } from '../auth/auth.middleware.js';
import { toDate } from '../sales/documents.service.js';
import {
  createRenewalInvoice,
  renewalInputSchema,
  renewalView,
  runDueRecurring,
  saveTemplate,
  scheduleRenewalAlerts,
  templateInputSchema,
  templateView,
  upcomingDates,
  addMonthsAnchored,
} from './recurring.service.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

export function createAutomationRouter({ db, config }: Deps) {
  const router = Router();
  router.use(['/sales/recurring', '/sales/renewals'], authenticate({ db, config }), idempotent(db));

  const actor = (req: Request) => ({
    companyId: req.auth!.companyId,
    userId: req.auth!.userId,
    ip: clientIp(req),
  });
  const need = (req: Request, action: string) => {
    if (!permissionsGranted(req.auth!.permissions, `sales.recurring.${action}`))
      throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
  };
  const names = async (ids: number[]) =>
    new Map(
      (
        await db.party.findMany({
          where: { id: { in: [...new Set(ids)] } },
          select: { id: true, name: true },
        })
      ).map((p) => [p.id, p.name]),
    );
  /** Own-data users may only schedule for clients they own. */
  const assertPartyVisible = async (req: Request, partyId: number) => {
    if (req.auth!.scope !== 'own') return;
    const p = await db.party.findFirst({
      where: { id: partyId, companyId: req.auth!.companyId, ownerId: req.auth!.userId },
    });
    if (!p) throw new HttpError(403, 'FORBIDDEN', 'You can only use your own clients');
  };
  const tScope = (req: Request): Prisma.RecurringTemplateWhereInput => ({
    companyId: req.auth!.companyId,
    ...(req.auth!.scope === 'own' ? { ownerId: req.auth!.userId } : {}),
  });
  const rScope = (req: Request): Prisma.RenewalWhereInput => ({
    companyId: req.auth!.companyId,
    ...(req.auth!.scope === 'own' ? { ownerId: req.auth!.userId } : {}),
  });

  // ───────────── recurring templates ─────────────
  router.get(
    '/sales/recurring',
    asyncHandler(async (req, res) => {
      need(req, 'view');
      const rows = await db.recurringTemplate.findMany({
        where: tScope(req),
        orderBy: { id: 'desc' },
        take: 200,
      });
      const n = await names(rows.map((r) => r.partyId));
      res.json({
        items: rows.map((t) => ({
          ...templateView(t, n.get(t.partyId)),
          upcoming: upcomingDates(t),
        })),
      });
    }),
  );
  router.post(
    '/sales/recurring',
    asyncHandler(async (req, res) => {
      need(req, 'create');
      const input = parse(templateInputSchema, req.body);
      await assertPartyVisible(req, input.partyId);
      const t = await db.$transaction(async (tx) => {
        const created = await saveTemplate(tx, {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          input,
        });
        await audit(tx, actor(req), {
          action: 'recurring.created',
          entity: 'recurring_template',
          entityId: created.id,
          after: { name: created.name, frequency: created.frequency },
        });
        return created;
      });
      res.status(201).json(templateView(t));
    }),
  );
  router.put(
    '/sales/recurring/:id',
    asyncHandler(async (req, res) => {
      need(req, 'edit');
      const input = parse(templateInputSchema, req.body);
      const own = await db.recurringTemplate.findFirst({
        where: { id: Number(req.params.id), ...tScope(req) },
      });
      if (!own) throw new HttpError(404, 'NOT_FOUND', 'Template not found');
      const t = await db.$transaction(async (tx) => {
        const saved = await saveTemplate(tx, {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          input,
          id: own.id,
        });
        await audit(tx, actor(req), {
          action: 'recurring.updated',
          entity: 'recurring_template',
          entityId: saved.id,
          after: { name: saved.name },
        });
        return saved;
      });
      res.json(templateView(t));
    }),
  );
  for (const [action, status] of [
    ['pause', 'paused'],
    ['resume', 'active'],
  ] as const) {
    router.post(
      `/sales/recurring/:id/${action}`,
      asyncHandler(async (req, res) => {
        need(req, 'edit');
        const own = await db.recurringTemplate.findFirst({
          where: { id: Number(req.params.id), ...tScope(req) },
        });
        if (!own) throw new HttpError(404, 'NOT_FOUND', 'Template not found');
        if (own.status === 'ended')
          throw new HttpError(409, 'INVALID_STATE', 'This schedule has ended');
        const t = await db.recurringTemplate.update({ where: { id: own.id }, data: { status } });
        await audit(db, actor(req), {
          action: `recurring.${action}d`,
          entity: 'recurring_template',
          entityId: own.id,
        });
        res.json(templateView(t));
      }),
    );
  }
  router.post(
    '/sales/recurring/:id/run-now',
    asyncHandler(async (req, res) => {
      need(req, 'edit');
      const own = await db.recurringTemplate.findFirst({
        where: { id: Number(req.params.id), ...tScope(req) },
      });
      if (!own) throw new HttpError(404, 'NOT_FOUND', 'Template not found');
      if (own.status !== 'active')
        throw new HttpError(409, 'INVALID_STATE', 'Resume the schedule first');
      // Brings the next date forward to today so the normal idempotent path creates the invoice.
      const today = toDate(new Date().toISOString().slice(0, 10));
      if (own.nextRunDate > today)
        await db.recurringTemplate.update({ where: { id: own.id }, data: { nextRunDate: today } });
      const results = await runDueRecurring(db, config, new Date(), own.id);
      res.json({ results });
    }),
  );
  router.get(
    '/sales/recurring/:id/runs',
    asyncHandler(async (req, res) => {
      need(req, 'view');
      const own = await db.recurringTemplate.findFirst({
        where: { id: Number(req.params.id), ...tScope(req) },
      });
      if (!own) throw new HttpError(404, 'NOT_FOUND', 'Template not found');
      const runs = await db.recurringRun.findMany({
        where: { templateId: own.id },
        orderBy: { runDate: 'desc' },
        take: 50,
      });
      const invs = await db.salesDocument.findMany({
        where: { id: { in: runs.map((r) => r.invoiceId).filter((x): x is number => !!x) } },
        select: { id: true, number: true, status: true },
      });
      res.json({
        items: runs.map((r) => ({
          id: r.id,
          runDate: r.runDate.toISOString().slice(0, 10),
          status: r.status,
          note: r.note,
          invoice: invs.find((i) => i.id === r.invoiceId) ?? null,
        })),
      });
    }),
  );

  // ───────────── renewal tracker ─────────────
  router.get(
    '/sales/renewals',
    asyncHandler(async (req, res) => {
      need(req, 'view');
      const q = req.query as Record<string, string | undefined>;
      const today = toDate(new Date().toISOString().slice(0, 10));
      const days = Number(q.withinDays) || 0;
      const where: Prisma.RenewalWhereInput = {
        AND: [
          rScope(req),
          q.status ? { status: q.status } : {},
          q.filter === 'expired' ? { status: 'active', expiryDate: { lt: today } } : {},
          days
            ? {
                status: 'active',
                expiryDate: { gte: today, lte: new Date(today.getTime() + days * 86_400_000) },
              }
            : {},
          q.search ? { OR: [{ identifier: { contains: q.search } }] } : {},
          q.partyId ? { partyId: Number(q.partyId) } : {},
        ],
      };
      const rows = await db.renewal.findMany({ where, orderBy: { expiryDate: 'asc' }, take: 300 });
      const n = await names(rows.map((r) => r.partyId));
      res.json({ items: rows.map((r) => renewalView(r, n.get(r.partyId))) });
    }),
  );
  router.post(
    '/sales/renewals',
    asyncHandler(async (req, res) => {
      need(req, 'create');
      const input = parse(renewalInputSchema, req.body);
      await assertPartyVisible(req, input.partyId);
      const party = await db.party.findFirst({
        where: { id: input.partyId, companyId: req.auth!.companyId, active: true },
      });
      if (!party || !party.isClient)
        throw new HttpError(400, 'VALIDATION_ERROR', 'Choose a client');
      if (input.expiryDate <= input.startDate)
        throw new HttpError(
          400,
          'VALIDATION_ERROR',
          'The expiry date must be after the start date',
        );
      const r = await db.renewal.create({
        data: {
          ...input,
          companyId: req.auth!.companyId,
          startDate: toDate(input.startDate),
          expiryDate: toDate(input.expiryDate),
          ownerId: input.ownerId ?? req.auth!.userId,
        },
      });
      await scheduleRenewalAlerts(db, r);
      await audit(db, actor(req), {
        action: 'renewal.created',
        entity: 'renewal',
        entityId: r.id,
        after: { identifier: r.identifier },
      });
      res.status(201).json(renewalView(r, party.name));
    }),
  );
  router.put(
    '/sales/renewals/:id',
    asyncHandler(async (req, res) => {
      need(req, 'edit');
      const input = parse(renewalInputSchema, req.body);
      const own = await db.renewal.findFirst({
        where: { id: Number(req.params.id), ...rScope(req) },
      });
      if (!own) throw new HttpError(404, 'NOT_FOUND', 'Renewal not found');
      const r = await db.renewal.update({
        where: { id: own.id },
        data: {
          ...input,
          startDate: toDate(input.startDate),
          expiryDate: toDate(input.expiryDate),
          ownerId: input.ownerId ?? own.ownerId,
        },
      });
      await scheduleRenewalAlerts(db, r);
      await audit(db, actor(req), { action: 'renewal.updated', entity: 'renewal', entityId: r.id });
      res.json(renewalView(r));
    }),
  );
  router.post(
    '/sales/renewals/:id/invoice',
    asyncHandler(async (req, res) => {
      need(req, 'edit');
      if (!permissionsGranted(req.auth!.permissions, 'sales.invoice.create'))
        throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to create invoices');
      const own = await db.renewal.findFirst({
        where: { id: Number(req.params.id), ...rScope(req) },
      });
      if (!own) throw new HttpError(404, 'NOT_FOUND', 'Renewal not found');
      const inv = await createRenewalInvoice(db, own, req.auth!);
      await audit(db, actor(req), {
        action: 'renewal.invoiced',
        entity: 'renewal',
        entityId: own.id,
        after: { invoiceId: inv.id },
      });
      res.status(201).json({ invoiceId: inv.id });
    }),
  );
  router.post(
    '/sales/renewals/:id/renew',
    asyncHandler(async (req, res) => {
      need(req, 'edit');
      const own = await db.renewal.findFirst({
        where: { id: Number(req.params.id), ...rScope(req) },
      });
      if (!own) throw new HttpError(404, 'NOT_FOUND', 'Renewal not found');
      if (own.status !== 'active')
        throw new HttpError(409, 'INVALID_STATE', 'Only active renewals can be renewed');
      const next = addMonthsAnchored(own.expiryDate, own.termMonths, own.expiryDate.getUTCDate());
      const r = await db.renewal.update({
        where: { id: own.id },
        data: { startDate: own.expiryDate, expiryDate: next, lastInvoiceId: null },
      });
      await scheduleRenewalAlerts(db, r);
      await audit(db, actor(req), {
        action: 'renewal.renewed',
        entity: 'renewal',
        entityId: r.id,
        after: { expiryDate: r.expiryDate.toISOString().slice(0, 10) },
      });
      res.json(renewalView(r));
    }),
  );
  router.post(
    '/sales/renewals/:id/cancel',
    asyncHandler(async (req, res) => {
      need(req, 'delete');
      parse(z.object({ reason: z.string().trim().min(3).max(300) }), req.body);
      const own = await db.renewal.findFirst({
        where: { id: Number(req.params.id), ...rScope(req) },
      });
      if (!own) throw new HttpError(404, 'NOT_FOUND', 'Renewal not found');
      const r = await db.renewal.update({ where: { id: own.id }, data: { status: 'cancelled' } });
      await scheduleRenewalAlerts(db, r);
      await audit(db, actor(req), {
        action: 'renewal.cancelled',
        entity: 'renewal',
        entityId: r.id,
        after: { reason: req.body.reason },
      });
      res.json(renewalView(r));
    }),
  );

  return router;
}
